import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { useKnowledgeTableSort } from "modules/backoffice/lib/knowledge/useKnowledgeTableSort"
import { useTranslation } from "react-i18next"
import { BackofficeKnowledgeCapabilitiesResponseDataSchema } from "schema"
import { z } from "zod"
import { BackofficeKnowledgeSection } from "./BackofficeKnowledgeSection"

type Capabilities = z.infer<typeof BackofficeKnowledgeCapabilitiesResponseDataSchema>

type Props = {
  data: Capabilities | undefined
  isPending: boolean
}

const COLUMN_COUNT = 4

export function BackofficeKnowledgeCapabilitiesTable({ data, isPending }: Props) {
  const { t } = useTranslation()
  const { sortedData, sortable } = useKnowledgeTableSort(data, {
    name: (row) => row.name ?? row.identifier,
    identifier: (row) => row.identifier,
    messageCount: (row) => row.messageCount,
    answeredQuestionCount: (row) => row.answeredQuestionCount,
  })

  function renderContent() {
    if (isPending || data === undefined) {
      return <TableSkeleton colSpans={Array.from({ length: COLUMN_COUNT }, () => 1)} />
    }

    if (data.length === 0) {
      return (
        <TableEmptySlate
          colSpan={COLUMN_COUNT}
          message={t("backoffice.knowledge.capabilities.empty")}
        />
      )
    }

    return sortedData.map((capability) => {
      const name = capability.name ?? capability.identifier

      return (
        <TableRow key={capability.capabilityId}>
          <TableCell>
            <span className="block max-w-md truncate" title={name}>
              {name}
            </span>
          </TableCell>
          <TableCell>
            <span className="font-mono text-xs">{capability.identifier}</span>
          </TableCell>
          <TableCell align="right">{capability.messageCount}</TableCell>
          <TableCell align="right">{capability.answeredQuestionCount}</TableCell>
        </TableRow>
      )
    })
  }

  return (
    <BackofficeKnowledgeSection
      title={t("backoffice.knowledge.capabilities.title")}
      description={t("backoffice.knowledge.capabilities.description")}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead sortable={sortable("name")}>
              {t("backoffice.knowledge.capabilities.name")}
            </TableHead>
            <TableHead sortable={sortable("identifier")}>
              {t("backoffice.knowledge.capabilities.identifier")}
            </TableHead>
            <TableHead align="right" sortable={sortable("messageCount")}>
              {t("backoffice.knowledge.capabilities.messageCount")}
            </TableHead>
            <TableHead align="right" sortable={sortable("answeredQuestionCount")}>
              {t("backoffice.knowledge.capabilities.answeredQuestionCount")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>{renderContent()}</TableBody>
      </Table>
    </BackofficeKnowledgeSection>
  )
}
